import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import BottomNav from "@/components/BottomNav";
import FixtureCard from "@/components/FixtureCard";
import { endpoints } from "@/lib/api";
import { useLive } from "@/lib/useLive";

export default function GamesPage() {
  const [fixtures, setFixtures] = useState([]);
  const [week, setWeek] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [fs, st] = await Promise.all([
        endpoints.fixtures(),
        endpoints.tournamentStatus(),
      ]);
      setFixtures(fs);
      setWeek(st.current_week);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useLive(() => { load(); });

  const groups = {
    live: fixtures.filter((f) => f.status === "live"),
    scheduled: fixtures.filter((f) => f.status === "scheduled"),
    completed: [...fixtures.filter((f) => f.status === "completed")].sort((a, b) => {
      const ta = a.completed_at || a.started_at || a.created_at || "";
      const tb = b.completed_at || b.started_at || b.created_at || "";
      return tb.localeCompare(ta);
    }),
  };

  return (
    <div className="app-shell">
      <AppHeader week={week} />
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 pb-28 pt-4">
        <SectionTitle count={groups.live.length}>Live now</SectionTitle>
        {groups.live.length === 0 ? (
          <EmptyLine text="No live fixtures. Check back soon." />
        ) : (
          <div className="grid gap-3">
            {groups.live.map((f) => <FixtureCard key={f.id} f={f} />)}
          </div>
        )}

        <SectionTitle count={groups.scheduled.length} className="mt-8">
          Upcoming
        </SectionTitle>
        {groups.scheduled.length === 0 ? (
          <EmptyLine text="No fixtures scheduled." />
        ) : (
          <WeekGroups fixtures={groups.scheduled} order="asc" />
        )}

        <SectionTitle count={groups.completed.length} className="mt-8">
          Results
        </SectionTitle>
        {groups.completed.length === 0 ? (
          <EmptyLine text="No completed fixtures yet." />
        ) : (
          <WeekGroups fixtures={groups.completed} order="desc" />
        )}

        {loading && <EmptyLine text="Loading…" />}

        <div className="mt-10 flex flex-col items-center justify-center gap-3">
          <Link
            to="/ref"
            data-testid="ref-link"
            className="text-[10px] font-semibold uppercase tracking-[0.35em] text-muted-foreground hover:text-primary"
          >
            Referee access
          </Link>
          <a
            href="https://www.beeyond.agency"
            target="_blank"
            rel="noreferrer"
            data-testid="footer-credit"
            className="text-[10px] font-semibold uppercase tracking-[0.35em] text-muted-foreground hover:text-primary"
          >
            Developed by Beeyond Tech
          </a>
        </div>
      </main>
      <BottomNav />
    </div>
  );
}

function WeekGroups({ fixtures, order = "asc" }) {
  const byWeek = new Map();
  for (const f of fixtures) {
    const wk = f.week_number ?? 0;
    if (!byWeek.has(wk)) byWeek.set(wk, []);
    byWeek.get(wk).push(f);
  }
  const weeks = [...byWeek.keys()].sort((a, b) => (order === "asc" ? a - b : b - a));
  return (
    <div className="grid gap-8">
      {weeks.map((wk) => {
        const items = [...byWeek.get(wk)].sort((a, b) => (a.court_number ?? 0) - (b.court_number ?? 0));
        const date = items[0]?.scheduled_at;
        return (
          <section key={wk} data-testid={`week-group-${wk}`}>
            <div className="mb-3 flex items-center gap-3">
              <h3 className="display text-sm font-black uppercase tracking-[0.25em] text-muted-foreground">
                Week {wk}
              </h3>
              {date && (
                <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-primary num-mono">
                  {formatDate(date)}
                </span>
              )}
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid gap-3">
              {items.map((f) => <FixtureCard key={f.id} f={f} />)}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function formatDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  if (!m) return iso;
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  return `${Number(m[3])} ${months[Number(m[2]) - 1]}`.toUpperCase();
}

function SectionTitle({ children, count, className = "" }) {
  return (
    <div className={`mb-3 flex items-baseline justify-between ${className}`}>
      <h2 className="display text-xl font-black uppercase tracking-tight">
        {children}
      </h2>
      <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground num-mono">
        {count}
      </span>
    </div>
  );
}

function EmptyLine({ text }) {
  return (
    <div className="rounded-sm border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}
